import React, { useEffect, useState } from 'react';
import {
  formatValor, formatVariacao, formatVariacaoPct, sinalVariacao, formatDataHora
} from '../utils/dreFormat';

// Lista dos 7 indicadores do PDCA (orçado/realizado/variação) com a justificativa
// de cada um. Quando `editable`, cada justificativa é um editor que faz upsert
// via `onSaveJustificativa(tipoIndicador, descricao)` (Promise). Caso contrário,
// mostra o texto em modo leitura (drilldown regional / diretoria).
function DreIndicadores({ indicadores, justificativas, editable, onSaveJustificativa }) {
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [feedback, setFeedback] = useState({}); // key -> { tipo: 'ok'|'erro', msg }

  // Sincroniza rascunhos com as justificativas carregadas (troca de período).
  useEffect(() => {
    const next = {};
    for (const ind of indicadores) {
      next[ind.indicador] = (justificativas[ind.indicador] || {}).descricao || '';
    }
    setDrafts(next);
    setFeedback({});
  }, [indicadores, justificativas]);

  const handleSave = async (tipoIndicador) => {
    const descricao = (drafts[tipoIndicador] || '').trim();
    if (!descricao) {
      setFeedback(f => ({ ...f, [tipoIndicador]: { tipo: 'erro', msg: 'Justificativa vazia' } }));
      return;
    }
    setSavingKey(tipoIndicador);
    setFeedback(f => ({ ...f, [tipoIndicador]: null }));
    try {
      await onSaveJustificativa(tipoIndicador, descricao);
      setFeedback(f => ({ ...f, [tipoIndicador]: { tipo: 'ok', msg: 'Salvo' } }));
    } catch (err) {
      setFeedback(f => ({ ...f, [tipoIndicador]: { tipo: 'erro', msg: err.message || 'Erro ao salvar' } }));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="dre-indicadores">
      {indicadores.map(ind => {
        const just = justificativas[ind.indicador] || null;
        const pct = formatVariacaoPct(ind.orcado, ind.realizado);
        const sinal = sinalVariacao(ind.orcado, ind.realizado);
        const fb = feedback[ind.indicador];

        return (
          <div key={ind.indicador} className="dre-card">
            <div className="dre-card-header">
              <span className="dre-card-title">{ind.label}</span>
            </div>

            <div className="dre-valores">
              <div className="dre-valor">
                <span className="dre-valor-label">Orçado</span>
                <span className="dre-valor-num">{formatValor(ind.orcado, ind.tipo)}</span>
              </div>
              <div className="dre-valor">
                <span className="dre-valor-label">Realizado</span>
                <span className="dre-valor-num">{formatValor(ind.realizado, ind.tipo)}</span>
              </div>
              <div className="dre-valor">
                <span className="dre-valor-label">Variação</span>
                <span className={`dre-valor-num dre-var-${sinal || 'na'}`}>
                  {formatVariacao(ind.orcado, ind.realizado, ind.tipo)}
                  {pct && <span className="dre-var-pct"> ({pct})</span>}
                </span>
              </div>
            </div>

            <div className="dre-justificativa">
              <label className="dre-just-label">Justificativa</label>
              {editable ? (
                <>
                  <textarea
                    className="dre-just-input"
                    value={drafts[ind.indicador] || ''}
                    maxLength={5000}
                    rows={3}
                    placeholder="Descreva a justificativa deste indicador..."
                    onChange={e => setDrafts(d => ({ ...d, [ind.indicador]: e.target.value }))}
                  />
                  <div className="dre-just-actions">
                    <button
                      className="dre-save-button"
                      disabled={savingKey === ind.indicador}
                      onClick={() => handleSave(ind.indicador)}
                    >
                      {savingKey === ind.indicador ? 'Salvando...' : 'Salvar justificativa'}
                    </button>
                    {fb && <span className={`dre-feedback dre-feedback-${fb.tipo}`}>{fb.msg}</span>}
                    {just && just.dataAtualizacao && (
                      <span className="dre-just-meta">
                        Atualizado em {formatDataHora(just.dataAtualizacao)}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <p className="dre-just-readonly">
                  {just && just.descricao ? just.descricao : <em>Sem justificativa registrada.</em>}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default DreIndicadores;
